import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import reverse from 'lodash/reverse';
import some from 'lodash/some';

import TreeNode from '../models/TreeNode';
import { Grid, findPath } from './computeMinPathHelper';
import { GridStep, NodeSide, DirectionType } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { OPTION_NODE_TYPE } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { Point, IvrNode } from '../ivrTypes';
import findIndex from 'lodash/findIndex';

function calculateRect(source: IvrNode, target: IvrNode) {
  const delta = 500;

  const rectLeftPoint: Point = [
    source.x > target.x ? target.x - delta : source.x - delta,
    source.y > target.y ? target.y - delta : source.y - delta,
  ];
  const rectRightPoint: Point = [
    source.x > target.x ? source.x + source.width + delta : target.x + target.width + delta,
    source.y > target.y ? source.y + source.height + delta : target.y + target.height + delta,
  ];

  return [rectLeftPoint, rectRightPoint];
}

const getGrid = (source: IvrNode, target: IvrNode, nodes: IvrNode[]) => {
  const [rectLeftPoint, rectRightPoint] = calculateRect(source, target);

  const len = Math.round(Math.abs(rectRightPoint[0] - rectLeftPoint[0]) / GridStep);
  const height = Math.round(Math.abs(rectRightPoint[1] - rectLeftPoint[1]) / GridStep);

  const area = new Array(height);
  for (let y = 0; y < height; y++) {
    area[y] = new Array(len).fill(0);
  }
  const nodesInArea = nodes.filter((nd: IvrNode) => {
    const points = [
      [nd.x, nd.y],
      [nd.x + nd.width, nd.y],
      [nd.x, nd.y + nd.height],
      [nd.x + nd.width, nd.y + nd.height],
    ];
    if (nd.type === OPTION_NODE_TYPE) {
      return false;
    }
    return points.some(([x, y]) => {
      return rectLeftPoint[0] < x && rectRightPoint[0] > x && rectLeftPoint[1] < y && rectRightPoint[1] > y;
    });
  });
  nodesInArea.map((node: IvrNode) => {
    const delta = 1;
    const x0 = Math.round((node.x - rectLeftPoint[0] - delta) / GridStep);
    const y0 = Math.round((node.y - rectLeftPoint[1] - delta) / GridStep);
    const x1 = Math.round((node.x + node.width + delta - rectLeftPoint[0]) / GridStep);
    const y1 = Math.round((node.y + node.height + delta - rectLeftPoint[1]) / GridStep);
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        if (!area[y]) continue;
        area[y][x] = 1;
      }
    }
    return node;
  });
  return area;
};

const computePointInGrid = (point: Point, gridLeftTopPoint: Point) => [
  Math.round((point[0] - gridLeftTopPoint[0]) / GridStep),
  Math.round((point[1] - gridLeftTopPoint[1]) / GridStep),
];

const computePaths = (startPoint: Point, endPoint: Point, rect, area) => {
  const [rectLeftPoint] = rect;

  const start = computePointInGrid(startPoint, rectLeftPoint);
  const end = computePointInGrid(endPoint, rectLeftPoint);

  area[start[1]][start[0]] = 0;
  area[end[1]][end[0]] = 0;

  const [x0, y0] = [start[0], start[1]];
  const [x1, y1] = [end[0], end[1]];

  try {
    const path = findPath(x0, y0, x1, y1, new Grid(area));
    const optimizedPath: Point[] = optimizeLines(path as Point[]);
    return normalizePath(optimizedPath, rectLeftPoint, startPoint, endPoint);
  } catch (error) {
    return [];
  }
};

const normalizePath = (optimizedPath: Point[], shift: Point, startPoint: Point, endPoint: Point) => {
  if (!optimizedPath.length) {
    return [];
  }
  const deltaX = optimizedPath[0][0] * GridStep + shift[0] - startPoint[0];
  const deltaY = optimizedPath[0][1] * GridStep + shift[1] - startPoint[1];

  const shifted: Point[] = optimizedPath.map((point: Point) => {
    return [point[0] * GridStep + shift[0] - deltaX, point[1] * GridStep + shift[1] - deltaY];
  });

  if (shifted.length < 2) {
    return shifted;
  }
  // adjust start and end point
  shifted[0] = startPoint;
  const direction = getDirection(shifted.length - 1, shifted);

  shifted[shifted.length - 1] = endPoint;
  if (direction === DirectionType.Horizontal) {
    shifted[shifted.length - 2][1] = endPoint[1];
  } else {
    shifted[shifted.length - 2][0] = endPoint[0];
  }

  return shifted;
};

const getDirection = (index: number, path: Point[]): DirectionType => {
  return path[index - 1][0] === path[index][0]
    ? DirectionType.Vertical
    : path[index - 1][1] === path[index][1]
    ? DirectionType.Horizontal
    : DirectionType.Diagonal;
};

const optimizeLines = (path: Point[]): Point[] => {
  if (!path.length) return [];

  const optimizedPath: Point[] = [];

  optimizedPath.push(path[0]);
  let direction: number = getDirection(1, path);
  for (let i = 1; i <= path.length; i++) {
    if (i === path.length) {
      optimizedPath.push(path[i - 1]);
      break;
    }
    const newDirection = getDirection(i, path);
    if (newDirection !== direction) {
      optimizedPath.push(path[i - 1]);
    }
    direction = newDirection;
  }
  return optimizedPath;
};

const pathLength = (path: Point[]): number => {
  const { length } = path.reduce(
    (acc: any, point: Point) => {
      if (acc.prior) {
        const length = Math.sqrt(
          Math.pow(Math.abs(acc.prior[1] - point[1]), 2) + Math.pow(Math.abs(acc.prior[0] - point[0]), 2)
        );
        acc.length += length;
      }
      acc.prior = point;

      return acc;
    },
    {
      length: 0,
      prior: null,
    }
  );
  return length;
};

const getTouchPointForNodeConnectedByReference = (
  side: NodeSide,
  source: TreeNode,
  target: TreeNode,
  option,
  optionIndex
): [number, number] => {
  const connectedPoints = target.connectionPoints[side];

  const refsCount = connectedPoints ? connectedPoints.length : 1;
  const refIndex = findIndex(
    connectedPoints,
    (point: any) => point.sourceId === source.id && point.option === option && point.optionIndex === optionIndex
  );

  let part: number;

  const referenceIndex = refIndex === -1 ? 0 : refIndex;
  if ([NodeSide.LEFT, NodeSide.RIGHT].indexOf(side) !== -1) {
    part = target.height / (refsCount + 1);

    return [target[side][0], target.y + part * (referenceIndex + 1)];
  } else {
    part = target.width / (refsCount + 1);
  }
  return [target.x + part * (referenceIndex + 1), target[side][1]];
};

const getAllPossibleConnectors = (source: IvrNode, target: IvrNode, option, optionIndex) => {
  const points = [NodeSide.RIGHT, NodeSide.LEFT, NodeSide.BOTTOM, NodeSide.TOP].reduce((acc: any, side: NodeSide) => {
    acc[side] = getTouchPointForNodeConnectedByReference(side, source, target, option, optionIndex);
    return acc;
  }, {});

  const toLeftSide = [
    [source.left, points[NodeSide.LEFT], NodeSide.LEFT, NodeSide.LEFT],
    [source.left, points[NodeSide.RIGHT], NodeSide.LEFT, NodeSide.RIGHT],
    [source.left, points[NodeSide.TOP], NodeSide.LEFT, NodeSide.TOP],
    [source.left, points[NodeSide.BOTTOM], NodeSide.LEFT, NodeSide.BOTTOM],
  ];

  const toRightSide = [
    [source.right, points[NodeSide.RIGHT], NodeSide.RIGHT, NodeSide.RIGHT],
    [source.right, points[NodeSide.LEFT], NodeSide.RIGHT, NodeSide.LEFT],
    [source.right, points[NodeSide.TOP], NodeSide.RIGHT, NodeSide.TOP],
    [source.right, points[NodeSide.BOTTOM], NodeSide.RIGHT, NodeSide.BOTTOM],
  ];

  const toTopSide = [
    [source.top, points[NodeSide.RIGHT], NodeSide.TOP, NodeSide.RIGHT],
    [source.top, points[NodeSide.LEFT], NodeSide.TOP, NodeSide.LEFT],
    [source.top, points[NodeSide.TOP], NodeSide.TOP, NodeSide.TOP],
    [source.top, points[NodeSide.BOTTOM], NodeSide.TOP, NodeSide.BOTTOM],
  ];
  const toBottomSide = [
    [source.bottom, points[NodeSide.LEFT], NodeSide.BOTTOM, NodeSide.LEFT],
    [source.bottom, points[NodeSide.RIGHT], NodeSide.BOTTOM, NodeSide.RIGHT],
    [source.bottom, points[NodeSide.TOP], NodeSide.BOTTOM, NodeSide.TOP],
    [source.bottom, points[NodeSide.BOTTOM], NodeSide.BOTTOM, NodeSide.BOTTOM],
  ];

  let pointsToCalculatePath: any[] = [];
  pointsToCalculatePath = [...pointsToCalculatePath, ...toBottomSide, ...toRightSide, ...toTopSide, ...toLeftSide];

  return pointsToCalculatePath;
};

const computeAllPath = (
  source: IvrNode,
  target: IvrNode,
  option: string | undefined,
  optionIndex: number | undefined,
  nodes: IvrNode[]
) => {
  const rect = calculateRect(source, target);
  const area = getGrid(source, target, nodes);
  return getAllPossibleConnectors(source, target, option, optionIndex).reduce((acc: any, point: any) => {
    acc[`${point[2]}x${point[3]}`] = computePaths(point[0], point[1], rect, area);
    // reverse path
    acc[`${point[2]}x${point[3]}xReverse`] = computePaths(point[1], point[0], rect, area);
    return acc;
  }, {});
};

const getMinPath = (
  source: IvrNode,
  target: IvrNode,
  option: string | undefined,
  optionIndex: number | undefined,
  nodes: IvrNode[]
): [Point[], NodeSide, NodeSide] => {
  const allPaths = computeAllPath(source, target, option, optionIndex, nodes);

  let points: any = [];
  let minPointsCount: number | undefined;
  let minPathLength: number;
  let sourcePointSide;
  let targetPointSide;

  for (const line in allPaths) {
    const ln: any = allPaths[line];
    if (!ln.length) {
      continue;
    }
    const length = pathLength(ln);

    const [sourcePoint, targetPoint, lineReversed] = line.split('x');

    if (isPathForbiddenToUse(lineReversed, sourcePoint, targetPoint, ln)) {
      continue;
    }

    if (!minPointsCount || (minPathLength! > length && minPointsCount >= ln.length)) {
      points = lineReversed ? reverse(ln) : ln;
      minPathLength = length;
      minPointsCount = ln.length;
      sourcePointSide = sourcePoint;
      targetPointSide = targetPoint;
    }
  }

  return [points, sourcePointSide, targetPointSide];
};

const isPathForbiddenToUse = (reverse, sourcePoint, targetPoint, ln) => {
  const hasVerticalEnd = (ln: Point[]) => {
    return ln[ln.length - 2][0] === ln[ln.length - 1][0];
  };
  const hasVerticalStart = (ln: Point[]) => {
    return ln[0][0] === ln[1][0];
  };

  const fn = (nodeSide: NodeSide, reverse: boolean) =>
    [
      [[NodeSide.RIGHT, NodeSide.LEFT], !reverse, hasVerticalEnd(ln)],
      [[NodeSide.RIGHT, NodeSide.LEFT], reverse, hasVerticalStart(ln)],
      [[NodeSide.TOP, NodeSide.BOTTOM], !reverse, !hasVerticalEnd(ln)],
      [[NodeSide.TOP, NodeSide.BOTTOM], reverse, !hasVerticalStart(ln)],
    ].some(([sides, directionReversed, lineDirection]: any) => {
      return sides.includes(nodeSide as NodeSide) && directionReversed && lineDirection;
    });

  const fn1 = (nodeSide: NodeSide) => {
    return [
      ln[1][1] - ln[0][1] > 1 && nodeSide === NodeSide.TOP && hasVerticalStart(ln),
      ln[1][1] - ln[0][1] < 1 && nodeSide === NodeSide.BOTTOM && hasVerticalStart(ln),
    ].some(result => result);
  };

  return fn(targetPoint, reverse) || fn(sourcePoint, !reverse) || fn1(sourcePoint);
};

const getSvgLine = (path: Point[], radius: number, showArrow: boolean): string => {
  let line = '';

  path.forEach((point: Point, index: number) => {
    if (!line) {
      line = `M${point[0]},${point[1]}`;
    }
    if (index === path.length - 1) {
      const direction = getDirection(index, path);
      const prevPoint = path[index - 1];
      // add shiftX,shiftY - we need decrise the latest line to show arrow
      // if we not shift the line the arrow will overlap the node
      const shiftX = showArrow && direction === DirectionType.Horizontal ? (prevPoint[0] < point[0] ? 2 : -2) : 0;
      const shiftY = showArrow && direction !== DirectionType.Horizontal ? (prevPoint[1] < point[1] ? 2 : -2) : 0;

      line += `L${point[0] - shiftX},${point[1] - shiftY}`;
    }
    if (index < path.length - 2) {
      const direction = getDirection(index + 1, path);
      const nextPoint = path[index + 1];

      if (direction === DirectionType.Horizontal) {
        const rX = nextPoint[0] > point[0] ? radius : -radius;
        const rY = path[index + 2][1] > point[1] ? radius : -radius;
        line += `L${nextPoint[0] - rX},${nextPoint[1]}`;
        line += `q ${rX},${0}  ${rX},${rY}`;
      }
      if (direction === DirectionType.Vertical) {
        const rY = nextPoint[1] > point[1] ? radius : -radius;
        const rX = path[index + 2][0] >= point[0] ? radius : -radius;

        line += `L${point[0]},${nextPoint[1] - rY}`;
        line += `q ${0},${rY}  ${rX},${rY}`;
      }
    }
  });
  return line;
};

const lineStraight = (
  source: TreeNode,
  target: TreeNode,
  option,
  optionIndex
): [Point[], NodeSide, NodeSide] | undefined => {
  const possibleLines = [
    [NodeSide.BOTTOM, NodeSide.TOP],
    [NodeSide.TOP, NodeSide.BOTTOM],
    [NodeSide.LEFT, NodeSide.RIGHT],
    [NodeSide.RIGHT, NodeSide.LEFT],
  ];
  const delta = 40;
  const link = find(possibleLines, ([sourcePointSide, targetPointSide]) => {
    if (
      (sourcePointSide === NodeSide.TOP && targetPointSide === NodeSide.BOTTOM && source.y < target.y) ||
      (sourcePointSide === NodeSide.BOTTOM && targetPointSide === NodeSide.TOP && source.y > target.y)
    ) {
      return false;
    }

    const sourcePoint = source[sourcePointSide];
    const targetPoint = getTouchPointForNodeConnectedByReference(targetPointSide, source, target, option, optionIndex);

    return (
      (sourcePoint[0] > targetPoint[0] - delta && sourcePoint[0] < targetPoint[0] + delta) ||
      (sourcePoint[1] > targetPoint[1] - delta && sourcePoint[1] < targetPoint[1] + delta)
    );
  });

  if (!link) {
    return;
  }

  const targetPoint1 = getTouchPointForNodeConnectedByReference(link[1], source, target, option, optionIndex);

  const targetPoint: any = link
    ? [NodeSide.BOTTOM, NodeSide.TOP].includes(link[1])
      ? [targetPoint1[0], targetPoint1[1]]
      : [target[link[1]][0], source[link[0]][1]]
    : null;

  const sourcePoint: any = link
    ? [NodeSide.BOTTOM, NodeSide.TOP].includes(link[0])
      ? [targetPoint1[0], source[link[0]][1]]
      : [target[link[1]][0], source[link[0]][1]]
    : null;

  return link && isPointBelongToNode(target, targetPoint, link[1]) && isPointBelongToNode(source, sourcePoint, link[0])
    ? [[sourcePoint, targetPoint], link[0], link[1]]
    : undefined;
};

const isPointBelongToNode = (node: TreeNode, point: Point, side: NodeSide) => {
  return [NodeSide.BOTTOM, NodeSide.TOP].includes(side)
    ? point[0] > node.x && point[0] < node.x + node.width
    : point[1] > node.y && point[1] < node.y + node.height;
};

const lineWith1Elbow = (source: TreeNode, target: TreeNode, option, optionIndex): [Point[], NodeSide, NodeSide] => {
  const nodeSides: [NodeSide, NodeSide] =
    source.y < target.y
      ? [NodeSide.BOTTOM, source.x > target.x ? NodeSide.RIGHT : NodeSide.LEFT]
      : [NodeSide.TOP, source.x > target.x ? NodeSide.RIGHT : NodeSide.LEFT];

  const targetConnectPoints = getTouchPointForNodeConnectedByReference(
    nodeSides[1],
    source,
    target,
    option,
    optionIndex
  );

  const points: [Point, Point] = [source[nodeSides[0]], targetConnectPoints];

  const start: Point = points[0];
  const end: Point = points[1];

  return [[start, [start[0], end[1]], end], nodeSides[0], nodeSides[1]];
};

const lineWith2Elbow = (source: TreeNode, target: TreeNode): [Point[], NodeSide, NodeSide] => {
  const start = source.bottom;
  const end = target.top;

  const sz = (end[1] - start[1]) / 5;
  return [[start, [start[0], start[1] + sz], [end[0], start[1] + sz], [end[0], end[1]]], NodeSide.BOTTOM, NodeSide.TOP];
};

const insideNode = (s: Point, e: Point, nodes: TreeNode[]) => {
  const isInside = some(nodes, node => {
    const nd = { x: node.x, y: node.y, width: node.width, height: node.height };

    const vertically =
      s[0] > nd.x &&
      e[0] < nd.x + nd.width &&
      ((s[1] > nd.y && s[1] < nd.y + nd.height) ||
        (e[1] > nd.y && e[1] < nd.y + nd.height) ||
        (s[1] < nd.y && e[1] > nd.y + nd.height) ||
        (s[1] >= nd.y && e[1] <= nd.y + nd.height));

    const horizontally =
      s[1] > nd.y &&
      e[1] < nd.y + nd.height &&
      ((s[0] > nd.x && s[0] < nd.x + nd.height) ||
        (e[0] > nd.x && e[0] < nd.x + nd.height) ||
        (s[0] < nd.x && e[0] > nd.x + nd.height));

    return vertically || horizontally;
  });

  return isInside;
};

const MIN_START_END_LINE_LENGTH = 10;
export const firstAndLastLineInPathIsNotToSmall = (path: Point[]) => {
  if (path.length < 2) {
    return false;
  }
  const firstLineLength = Math.abs(path[0][0] - path[1][0]) + Math.abs(path[0][1] - path[1][1]);
  const lastPosition = path.length - 1;
  const lastLineLength =
    Math.abs(path[lastPosition][0] - path[lastPosition - 1][0]) +
    Math.abs(path[lastPosition][1] - path[lastPosition - 1][1]);

  return firstLineLength > MIN_START_END_LINE_LENGTH && lastLineLength > MIN_START_END_LINE_LENGTH;
};

const isPathCrossNodes = (points: Point[], nodes: TreeNode[]) => {
  if (isEmpty(points)) {
    return false;
  }
  for (let i = 0; i < points.length - 1; i++) {
    const isCrossingNode = insideNode(points[i], points[i + 1], nodes);
    if (isCrossingNode) {
      return true;
    }
  }
  return false;
};

export {
  getMinPath,
  getSvgLine,
  getTouchPointForNodeConnectedByReference,
  isPathCrossNodes,
  lineStraight,
  lineWith1Elbow,
  lineWith2Elbow,
};
